import type { i18n as I18nInstance } from 'i18next';
import type {
  EducationContent,
  QuestionConfigBranchLoaderProps,
  QuestionConfigQuestionType,
  QuestionDataValue,
} from '/index';

import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import cityIcon from '/business/shared/images/icons/bp-clean-city-pictograms-bp-green-.svg';
import initiativeCardBackground from '/business/shared/images/initiatives/Buildings-card-1x.jpg';
import initiativeBackground from '/business/shared/images/initiatives/Buildings-card-2x.jpg';

import useFormatLeasedBuildingsSharedCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatLeasedBuildingsSharedCustom';
import useFormatLeasedBuildingsSharedSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatLeasedBuildingsSharedSupplier';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import educationContentJson from '/business/shared/components/questions/scope3/_education/downstreamLeasedBuildings.json';
import useLeasedBuildingsSchema from '/business/shared/components/questions/scope3/_hooks/useLeasedBuildingsSchema';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

type ConfigureDownstreamLeasedBuildingsOptions = {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
};

type LeasedBuildingTableData = {
  fuelUnit?: string | null;
  harmonisedInjection?: (unit: string) => string;
  quantity?: number | null;
  quantityUnit?: string | null;
  tonnage?: number | null;
};

type CombinedLeasedBuildingRow = {
  identifier: string;
  tonnage: number;
};

function isEducationContent(value: object): value is EducationContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'topParas' in value &&
    Array.isArray(value.topParas) &&
    'exampleParas' in value &&
    Array.isArray(value.exampleParas)
  );
}

function getEducationContent(): EducationContent {
  if (!isEducationContent(educationContentJson)) {
    throw new Error('Downstream leased buildings education content is not configured');
  }
  return educationContentJson;
}

function getStringValue(value: QuestionDataValue): string | null {
  return typeof value === 'string' ? value : null;
}

function getNumberValue(value: QuestionDataValue): number {
  return typeof value === 'number' ? value : 0;
}

function formatLeasedBuildingQuantity({
  fuelUnit = 'units',
  harmonisedInjection,
  i18n,
  quantity,
}: { i18n: I18nInstance } & LeasedBuildingTableData) {
  const unit = fuelUnit ?? 'units';
  const harmonisedUnit = harmonisedInjection ? ` ${harmonisedInjection(unit)}` : '';
  return `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(unit), unit)}${harmonisedUnit}`;
}

function formatDetailsQuantity({
  i18n,
  quantity,
  quantityUnit = 'units',
}: { i18n: I18nInstance } & LeasedBuildingTableData) {
  const unit = quantityUnit ?? 'units';
  return `${formatNumber(quantity)} ${i18n.t('business:emissionFactors.volumes.'.concat(unit), unit)}`;
}

export const getQuestionDownstreamLeasedBuildings = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = [], // this prop is reductionv2 ONLY, this question was developed after that
}: ConfigureDownstreamLeasedBuildingsOptions): QuestionConfigQuestionType => {
  return {
    id: 'downstream-leased-buildings',
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_3
      : questionScopeCategories.BUILDING_EMISSIONS,
    isCompletePropName: 'isDownstreamLeasedBuildingsComplete',
    apiKey: 'calculation-leased-buildings',
    title: i18n.t('business:QuestionPage.forms.emissionSelector.downstreamLeasedBuildings'),
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamLeasedBuildings',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamLeasedBuildingsTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamLeasedBuildings',
    reductionPageKey: 'downstreamLeasedBuildings',
    reductionPageCards: reductionCardsSlides,
    icon: cityIcon,
    useHookLoader: useLeasedBuildingsSchema,
    table: {
      title: i18n.t('business:Questions.calculations.downstreamLeasedBuildings.table.title'),
      totalType: basketTableTotalTypes.TONNAGE,
      headings: [
        'Identifier',
        i18n.t('business:Questions.calculations.question3.table.heading1'),
        i18n.t('business:Questions.calculations.question3.table.heading2'),
        'Carbon emissions',
      ],
      cells: [
        'identifier',
        'fuelType',
        ({ quantity, fuelUnit, harmonisedInjection }: LeasedBuildingTableData) =>
          formatLeasedBuildingQuantity({ i18n, quantity, fuelUnit, harmonisedInjection }),
        ({ tonnage }: LeasedBuildingTableData) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: false,
      hideDetailsButton: true,
      supplier: {
        columns: [
          'buildingIdentifier',
          'emissionSource',
          'supplierIntensityDataOrAbsoluteEmissions',
          'quantity',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['buildingIdentifier', 'emissionSource', 'intensityValue', 'unitOfMeasurement', 'carbonEmissions'],
      },
    },
    editPageTitle: i18n.t('business:Questions.calculations.downstreamLeasedBuildings.editTitle'),
    initiative: {
      getVisuals: ({ isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
      },

      formatForInitiativeSummary: ({ initiativeAnswerEnum = null } = {}) => {
        return {
          isShowingReportingPeriod: true,
          isShowingReportingPeriodNoReduction: false,
          category: initiativeAnswerEnum,
          description: `${initiativeAnswerEnum} consumption for leased buildings during the reporting period`,
          descriptionNoReduction: false,
        };
      },
    },
    details: {
      title: 'leasedBuildings',
      items: [
        'leasedBuildings',
        ({ quantity, quantityUnit }: LeasedBuildingTableData) =>
          formatDetailsQuantity({ i18n, quantity, quantityUnit }),
        ({ tonnage }: LeasedBuildingTableData) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.wasteOps.details.title'),
        },
      ],
    },
    selector: {
      tableViewSummaryRowParser: (allAnswerRows) => {
        const combined = allAnswerRows.reduce<CombinedLeasedBuildingRow[]>((prev, current) => {
          const genericData = current.answer?.genericData;
          const id =
            getStringValue(genericData?.identifier) ||
            getStringValue(genericData?.buildingIdentifier) ||
            getStringValue(genericData?.building_identifier);

          if (!id) {
            return prev;
          }
          const locatorIndex = prev.findIndex((search) => {
            return search.identifier === id;
          });
          if (locatorIndex === -1) {
            prev.push({
              identifier: id,
              tonnage: getNumberValue(current.answer?.tonnage),
            });
          } else {
            prev[locatorIndex].tonnage += getNumberValue(current.answer?.tonnage);
          }
          return prev;
        }, []);

        return combined.map((combinedRow) => {
          return {
            itemId: combinedRow.identifier,
            cells: [combinedRow.identifier, `${formatNumber(combinedRow.tonnage)} tCO\u2082e`],
            answer: {
              tonnage: combinedRow.tonnage,
            },
            title: null,
            type: 'downstream-leased-buildings',
          };
        });
      },
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with buildings leased to third party businesses who operated and were responsible for their energy consumption, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Building identifier, type of fuel, quantity used during the reporting period.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Building identifier, type of fuel, supplier name and intensity data/absolute emissions, quantity.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: 'Building identifier, emission source, intensity data/absolute emissions, quantity.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: getEducationContent(),
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatLeasedBuildingsSharedSupplier({
        ...props,
        questionFormatName: 'downstream-leased-buildings',
      }),
    useQuestionBranchCustomLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatLeasedBuildingsSharedCustom({ ...props, questionFormatName: 'downstream-leased-buildings' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the estimated energy and fuel consumption for any buildings owned by your business that was leased to a
          third party business who operated and were responsible for their energy consumption during the reporting
          period <ReportingPeriod $green />.
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What is the estimated energy and fuel consumption for any buildings owned by your business that was leased to
          a third party business who operated and were responsible for their energy consumption during the reporting
          period: <ReportingPeriod $green />?
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
